import { isNullOrWhitespace } from "@common/stringOperations";
import { EntityType } from "@common/Common.types";
import { ApprovalOption, GroupCompanyResponsible } from "../AnnualWheel.types";
import { AnnualWheelActivityFramework, AnnualWheelActivityGeneralType, AnnualWheelPriority } from "../annualWheelDialog/AnnualWheelDialog.types";
import { AnnualWheelActivitySubTask } from "../AnnualWheelSubTasks.types";
import { AnnualWheelActivitySaveModel } from "../AnnualWheelActivity.types";
import { RelationViewModel } from "../../taskManagement/createTaskPane/CreateTaskPane.types";
import { ActivityScheduleModel } from "./ActivityScheduleModel";

/** The sections that can hold an error, in the order the form shows them. */
export type ActivityFormSection = "basics" | "schedule" | "people";

export interface ActivityFormValues {
    name: string | null;
    description: string | null;
    subTasks: Array<AnnualWheelActivitySubTask>;
    // The entities mentioned in the description. They are copied into relations as they are mentioned.
    relationsFromDescription: Array<RelationViewModel>;
    customId: string | null | undefined;
    priority: AnnualWheelPriority | undefined;
    documentationRequired: boolean;
    schedule: ActivityScheduleModel;
    responsible: string | undefined;
    assignees: Array<string>;
    approval: ApprovalOption;
    // A processing activity validation leaves the user empty: each company is the scope of the validation
    // rather than a company with a responsible of its own.
    groupCompanies: Array<GroupCompanyResponsible>;
    generalType: AnnualWheelActivityGeneralType | undefined;
    areas: Array<string>;
    // A template names a framework rather than a compliance area. It is swapped for the matching area once
    // the areas are loaded, and cleared after that.
    frameWork: AnnualWheelActivityFramework | undefined;
    businessAreas: Array<string>;
    tags: Array<string>;
    relations: Array<RelationViewModel>;
    isProcessingActivityValidation: boolean;
    customTemplateId: string | undefined;
    standardTemplateId: string | undefined;
    contractId: string | undefined;
    contractDocumentId: string | undefined;
    section: string | undefined;
    clause: string | undefined;
}

export class ActivityFormModel implements ActivityFormValues {
    readonly name: string | null;
    readonly description: string | null;
    readonly subTasks: Array<AnnualWheelActivitySubTask>;
    readonly relationsFromDescription: Array<RelationViewModel>;
    readonly customId: string | null | undefined;
    readonly priority: AnnualWheelPriority | undefined;
    readonly documentationRequired: boolean;
    readonly schedule: ActivityScheduleModel;
    readonly responsible: string | undefined;
    readonly assignees: Array<string>;
    readonly approval: ApprovalOption;
    readonly groupCompanies: Array<GroupCompanyResponsible>;
    readonly generalType: AnnualWheelActivityGeneralType | undefined;
    readonly areas: Array<string>;
    readonly frameWork: AnnualWheelActivityFramework | undefined;
    readonly businessAreas: Array<string>;
    readonly tags: Array<string>;
    readonly relations: Array<RelationViewModel>;
    readonly isProcessingActivityValidation: boolean;
    readonly customTemplateId: string | undefined;
    readonly standardTemplateId: string | undefined;
    readonly contractId: string | undefined;
    readonly contractDocumentId: string | undefined;
    readonly section: string | undefined;
    readonly clause: string | undefined;

    constructor(values: ActivityFormValues) {
        this.name = values.name;
        this.description = values.description;
        this.subTasks = values.subTasks;
        this.relationsFromDescription = values.relationsFromDescription;
        this.customId = values.customId;
        this.priority = values.priority;
        this.documentationRequired = values.documentationRequired;
        this.schedule = values.schedule;
        this.responsible = values.responsible;
        this.assignees = values.assignees;
        this.approval = values.approval;
        this.groupCompanies = values.groupCompanies;
        this.generalType = values.generalType;
        this.areas = values.areas;
        this.frameWork = values.frameWork;
        this.businessAreas = values.businessAreas;
        this.tags = values.tags;
        this.relations = values.relations;
        this.isProcessingActivityValidation = values.isProcessingActivityValidation;
        this.customTemplateId = values.customTemplateId;
        this.standardTemplateId = values.standardTemplateId;
        this.contractId = values.contractId;
        this.contractDocumentId = values.contractDocumentId;
        this.section = values.section;
        this.clause = values.clause;
    }

    static create(values: Partial<ActivityFormValues>) {
        return new ActivityFormModel({
            name: null,
            description: null,
            subTasks: [],
            relationsFromDescription: [],
            customId: undefined,
            priority: AnnualWheelPriority.Moderate,
            documentationRequired: false,
            schedule: ActivityScheduleModel.create({}),
            responsible: undefined,
            assignees: [],
            approval: ApprovalOption.ApprovalNotRequired,
            groupCompanies: [],
            generalType: undefined,
            areas: [],
            frameWork: undefined,
            businessAreas: [],
            tags: [],
            relations: [],
            isProcessingActivityValidation: false,
            customTemplateId: undefined,
            standardTemplateId: undefined,
            contractId: undefined,
            contractDocumentId: undefined,
            section: undefined,
            clause: undefined,
            ...values,
        });
    }

    withUpdates(updates: Partial<ActivityFormValues>) {
        const updated = new ActivityFormModel({ ...this, ...updates });
        const changesMentions = "relationsFromDescription" in updates || "subTasks" in updates;

        return changesMentions ? updated.withMentionedRelations() : updated;
    }

    getFirstInvalidSection(): ActivityFormSection | undefined {
        if (this.isNameMissing()) {
            return "basics";
        }
        if (this.schedule.isInvalid()) {
            return "schedule";
        }
        // The companies of a processing activity validation carry out the validation themselves, so it needs no responsible.
        if (!this.isProcessingActivityValidation && this.isResponsibleMissing()) {
            return "people";
        }

        return undefined;
    }

    isNameMissing() {
        return isNullOrWhitespace(this.name);
    }

    // Every task needs someone to receive it: the responsible, or a responsible for each group company.
    isResponsibleMissing() {
        const hasResponsible = !isNullOrWhitespace(this.responsible);
        if (this.groupCompanies.length === 0) {
            return !hasResponsible;
        }

        return !hasResponsible && !this.allGroupCompaniesHaveResponsible();
    }

    allGroupCompaniesHaveResponsible() {
        return this.groupCompanies.every((groupCompany) => !isNullOrWhitespace(groupCompany.userId ?? undefined));
    }

    toSaveModel(annualWheelActivityId: string | undefined): AnnualWheelActivitySaveModel {
        const schedule = this.schedule;
        // Unticking the end date or switching to one-time leaves the end date fields as they were, so only a
        // shown end date is sent. The backend reads a missing end date as none.
        const hasEndDate = schedule.displayEndDate && schedule.isRecurring();

        return {
            name: this.name,
            description: this.description,
            subTasks: this.subTasks.map((subTask) => subTask.name),
            customId: this.customId,
            priority: this.priority,
            documentationRequired: this.documentationRequired,
            interval: schedule.interval,
            // A one-time template still defaults the type to Month, and the backend refuses a type without an interval.
            recurringIntervalType: schedule.isRecurring() ? schedule.recurrenceIntervalType : undefined,
            notificationPeriodInDays: schedule.notificationPeriodInDays,
            startDate: schedule.useRelativeStartDate ? null : schedule.startDate,
            endDate: hasEndDate && !schedule.useRelativeEndDate ? schedule.endDate : null,
            startDateRelativeConfig: schedule.useRelativeStartDate ? schedule.startDateRelativeConfig : undefined,
            endDateRelativeConfig: hasEndDate && schedule.useRelativeEndDate ? schedule.endDateRelativeConfig : undefined,
            responsible: isNullOrWhitespace(this.responsible) ? undefined : this.responsible,
            assignees: this.assignees,
            approval: this.approval,
            groupCompanies: this.groupCompanies,
            generalType: this.generalType,
            areas: this.areas,
            businessAreas: this.businessAreas,
            tags: this.tags,
            relations: this.relations.map((relation) => ({
                fromEntityId: annualWheelActivityId,
                fromEntityType: EntityType.AnnualWheelActivity,
                toEntityId: relation.entityId,
                toEntityType: relation.entityType,
            })),
            customTemplateId: this.customTemplateId,
            standardTemplateId: this.standardTemplateId,
            contractId: this.contractId,
            contractDocumentId: this.contractDocumentId,
            section: this.section,
            clause: this.clause,
        };
    }

    // Mentioning an entity in the description or a subtask relates the activity to it. Removing the mention
    // leaves the relation in place, so the user removes it from the relations list instead.
    private withMentionedRelations() {
        const mentionedRelations = [...this.relationsFromDescription, ...this.subTasks.flatMap((subTask) => subTask.relations)];
        const relations = [...this.relations];
        mentionedRelations.forEach((mentioned) => {
            if (!relations.some((relation) => relation.entityId === mentioned.entityId)) {
                relations.push(mentioned);
            }
        });

        return new ActivityFormModel({ ...this, relations });
    }
}
